import React, { useEffect, useState, useMemo, useLayoutEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { pushModifiersGroupData } from '@reducers/orders';
import { urlSearchParams } from '@utility/utills';
import { onClickModifierHelper } from '../OrderItemDetailHandler';
import ItemModifierGroup from './ItemModifierGroup';
import styles from '../OrderItemModifierCart/OrderItemModifierCart.module.scss';
import cn from 'classnames';

const OrderItemModifiers = ({
  modifierLevelData,
  modifierGroupsHashTable,
  activeMenuItemId,
  menuItemsHashTable,
  pushModifiersGroupData,
  menuCard,
  nestedModifierArray,
  modifierEditData,
  activeModGroup,
  isNestedModifier,
  showGlobalModButtons,
  modFunctionModifierGroupId,
  modifiersHashTable,
  serverAppTheme,
}) => {
  const { menuItemId, menuTypeId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [userExpandedGroups, setUserExpandedGroups] = useState({});
  const [userCollapsedGroups, setUserCollapsedGroups] = useState({});

  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [nestedModifierArray, isNestedModifier, menuItemId, activeModGroup]);

  useEffect(() => {
    setUserExpandedGroups({});
    setUserCollapsedGroups({});
  }, [menuItemId]);

  const handleGroupToggle = useCallback((id, isCollapsed) => {
    if (isCollapsed) {
      setUserCollapsedGroups(prev => ({ ...prev, [id]: true }));
      setUserExpandedGroups(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } else {
      setUserExpandedGroups(prev => ({ ...prev, [id]: true }));
      setUserCollapsedGroups(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }, []);

  const parentMenuItemId = useMemo(
    () => urlSearchParams('parentMenuItemId'),
    []
  );

  const currentMenuItem = useMemo(() => {
    return menuItemsHashTable[menuItemId];
  }, [menuItemsHashTable, menuItemId]);

  const isLinkedItemPage = useMemo(() => {
    if (!parentMenuItemId) return false;
    const parentItem = menuCard[menuTypeId]?.[parentMenuItemId];
    return parentItem?.menuItem?.links && parentItem.menuItem.links.length > 0;
  }, [parentMenuItemId, menuCard, menuTypeId]);

  const isSelectedLinkedItem = useMemo(() => {
    if (!isLinkedItemPage || !parentMenuItemId) return true; // Not a linked item page, always show modifiers
    return String(menuItemId) !== String(parentMenuItemId);
  }, [isLinkedItemPage, parentMenuItemId, menuItemId]);

  useEffect(() => {
    if (isNestedModifier) {
      setIsLoading(false);
      return;
    }
    if (isLinkedItemPage && !isSelectedLinkedItem) {
      pushModifiersGroupData([]).then(() => {
        setIsLoading(false);
      });
      return;
    }

    if (!currentMenuItem) {
      setIsLoading(false);
      return;
    }

    const hasModifiers =
      currentMenuItem.modifierGroupLinks &&
      currentMenuItem.modifierGroupLinks.length > 0;

    if (!hasModifiers) {
      pushModifiersGroupData([]).then(() => {
        setIsLoading(false);
      });
      return;
    }

    const modifierList = currentMenuItem.modifierGroupLinks.map(item => ({
      ...item,
      ...modifierGroupsHashTable[item.modifierGroupId],
      show: true,
    }));

    pushModifiersGroupData(modifierList).then(() => {
      setIsLoading(false);
    });
  }, [
    menuItemId,
    currentMenuItem,
    modifierGroupsHashTable,
    pushModifiersGroupData,
    isLinkedItemPage,
    isSelectedLinkedItem,
    isNestedModifier,
  ]);

  useLayoutEffect(() => {
    if (modifierEditData?.modifier) {
      onClickModifierHelper({
        modifier: modifierEditData.modifier,
        modifierGroup: modifierEditData.modifierGroupId,
        isActive: false,
        key: 'edit',
        nestedModifierArray: [...nestedModifierArray],
      });
    }
  }, [modifierEditData]);

  const hasActiveFilter = useMemo(() => activeModGroup && Object.values(activeModGroup).some(v => v), [activeModGroup]);

  const visibleModifierGroups = useMemo(() => {
    if (!modifierLevelData || modifierLevelData.length === 0) return [];
    return modifierLevelData.filter(modifierGroup => {
      if (
        modifierGroupsHashTable[modifierGroup.modifierGroupId]?.hiddenMenuId ===
        activeMenuItemId
      ) {
        return false;
      }
      if (hasActiveFilter) {
        return !!activeModGroup[modifierGroup.id];
      }
      return true;
    });
  }, [modifierLevelData, modifierGroupsHashTable, activeMenuItemId, activeModGroup, isNestedModifier, hasActiveFilter]);

  const isModifierRequired = useMemo(
    () => visibleModifierGroups.some(item => item.minimum > 0),
    [visibleModifierGroups]
  );

  const parentContext = useMemo(
    () =>
      nestedModifierArray.length > 0
        ? nestedModifierArray[nestedModifierArray.length - 1]
        : '',
    [nestedModifierArray]
  );

  const parentModifierName = useMemo(() => {
    if (isNestedModifier && parentContext) {
      const modifierId = parentContext.split('-')[0];
      return modifiersHashTable[modifierId]?.name || '';
    }
    return '';
  }, [isNestedModifier, parentContext, modifiersHashTable]);

  const isVerticalLayout =
    window.sessionStorage.getItem('serverAppModifierGroupDisplayStyle') == 1;

  const showVertical = isVerticalLayout && !isNestedModifier;

  const groups = visibleModifierGroups.map(modifierGroup => (
    <ItemModifierGroup
      key={`${menuItemId}-${parentContext}-${modifierGroup.id}`}
      modifierGroup={modifierGroup}
      isLoading={isLoading}
      isVerticalLayout={showVertical}
      hasActiveFilter={hasActiveFilter}
      userExpanded={!!userExpandedGroups[modifierGroup.id]}
      userCollapsed={!!userCollapsedGroups[modifierGroup.id]}
      onToggle={handleGroupToggle}
    />
  ));

  const containerHeightClass = useMemo(() => {
    if (
      isLinkedItemPage &&
      !showGlobalModButtons &&
      !modFunctionModifierGroupId
    ) {
      return 'h-76';
    }
    if (
      isLinkedItemPage &&
      modFunctionModifierGroupId &&
      !showGlobalModButtons
    ) {
      return 'h-75';
    }
    if (isLinkedItemPage) {
      return 'h-70';
    }
    if (
      !isLinkedItemPage &&
      modFunctionModifierGroupId &&
      !showGlobalModButtons
    ) {
      return 'h-86';
    }
    if (showGlobalModButtons) {
      return 'h-82';
    }
    if (modFunctionModifierGroupId) {
      return 'h-88';
    }
    return 'h-86';
  }, [isLinkedItemPage, showGlobalModButtons, modFunctionModifierGroupId]);

  return (
    groups?.length > 0 ? (
      <div
        ref={containerRef}
        className={cn(
          'px-0 w-100 overflowY',
          styles.OrderItemModifierWrapper,
          containerHeightClass
        )}
        id="modifiersContainer"
      >
        {parentModifierName && (
          <div
            className="fs_15 fw-600 pb-2 d-flex justify-content-center"
          >
            {parentModifierName}
          </div>
        )}
        {showVertical ? (
          <div className="col-12 p-0 h-100 d-flex overflowX overflowYHidden">
            {groups}
          </div>
        ) : (
          groups
        )}
      </div>
    ) : ''
  );
};

const mapStateToProps = ({ orders, theme }) => ({
  activeMenuItemId: Number(urlSearchParams('menuId')) || 0,
  menuItemsHashTable: orders.menuItemsHashTable,
  modifierLevelData: orders.modifierLevelData,
  modifierGroupsHashTable: orders.modifierGroupsHashTable,
  modifiersHashTable: orders.modifiersHashTable,
  menuCard: orders.menuCardList,
  nestedModifierArray: orders.nestedModifierArray,
  modifierEditData: orders.modifierEditData,
  isNestedModifier: orders.isNestedModifier,
  serverAppTheme: theme?.serverAppTheme,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ pushModifiersGroupData }, dispatch);
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(OrderItemModifiers));
